/**
 * How often to ask SL for new times, based on whether anyone is looking.
 *
 * Trafiklab asks for a departure board to refresh every two to five minutes,
 * and reckons a private project should sit near ten thousand requests a
 * month. Polling every thirty seconds, two stops open, is about eighteen
 * times that -- which is how this app taught itself what HTTP 429 looks like.
 *
 * But two minutes is a long time to stare at a board you just opened, so the
 * rate follows attention rather than being one number: quick while you are
 * newly there, slow once the app is merely still open. Nothing is lost by
 * slowing down, because the countdown on screen is computed locally and keeps
 * ticking between refreshes.
 *
 * A board left open on a desk therefore costs about 24 requests an hour per
 * stop instead of 120, and a pocketed PWA costs none at all -- TanStack does
 * not poll a hidden page.
 */

/** While you are newly looking. */
const FAST_MS = 30_000;
/** Once the app is just open. Inside Trafiklab's two-to-five minute advice. */
const SLOW_MS = 150_000;
/** How long "newly looking" lasts. */
const FAST_WINDOW_MS = 60_000;

let lastActiveAt = Date.now();

const markActive = () => {
  lastActiveAt = Date.now();
};

if (typeof window !== 'undefined') {
  // Focus and visibility both count: switching back to the tab and unlocking
  // the phone are the same intent, and TanStack refetches on focus anyway, so
  // this only decides how quickly the ones after that follow.
  window.addEventListener('focus', markActive);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) markActive();
  });
}

export function refreshIntervalMs(): number {
  return Date.now() - lastActiveAt < FAST_WINDOW_MS ? FAST_MS : SLOW_MS;
}
